/**
 * ReportComposerCanvas — M6 Output Factory · Increment 3 (2026-06-25, Anil).
 *
 * The PICK/DRAG Findings-Pool → report composer + a REAL, readable rendered
 * report preview. Tier-gated (mockup §3 + master map §3):
 *   L1 — auto-locked: the section set is FROZEN, no picking (fastest, consistent).
 *   L2 — curated: pre-selected, staff may add/remove a few.
 *   L3 — open: full pick-and-choose, build up across the engagement.
 * Only CONFIRMED / pool-ready findings are selectable. Source tags (auto/manual/
 * composite) carry onto the report sections.
 *
 * The readable report below is rendered from `buildReportSections` (the portal-
 * side mirror of the functions composer's new sections) so the owner can SEE a
 * real report in the preview. Production generation/delivery still goes through
 * the existing composeReport CF (now phase-aware) — this canvas is the
 * assembly + the readable view.
 */
import { useMemo, useState } from 'react';
import { Box, Btn3, Tag } from './ui';
import {
  buildReportSections,
  type ReportSection,
} from '../lib/reportContentBuilder';
import type { Finding } from '../lib/findingsModel';
import { phaseModelForTier, tierLevel } from '../lib/reportPhases';

export interface ReportComposerCanvasProps {
  tierCode: string;
  /** Intake answers (the structured data the report renders). */
  answers: Record<string, unknown>;
  /** The confirmed Findings Pool. */
  findings: Finding[];
  onMsg?: (m: string) => void;
}

const SOURCE_STYLE: Record<string, { bg: string; fg: string }> = {
  auto: { bg: 'var(--accent-wash, #eef3f8)', fg: 'var(--accent-deep, #274b63)' },
  manual: { bg: 'var(--purple-wash, #f0edf7)', fg: 'var(--purple, #5b4b8a)' },
  composite: { bg: 'var(--warn-wash, #fbf3e1)', fg: 'var(--warn, #9a6a12)' },
};

function SourceChip({ source }: { source: string }) {
  const s = SOURCE_STYLE[source] || SOURCE_STYLE.auto;
  return <span style={{ fontSize: 9, fontWeight: 700, borderRadius: 999, padding: '1px 6px', background: s.bg, color: s.fg }}>{source.toUpperCase()}</span>;
}

/** Render one section's structured body into readable HTML (no raw-HTML injection). */
function SectionBody({ section }: { section: ReportSection }) {
  return (
    <div>
      {section.body.map((row, i) => {
        if (row.table) {
          return (
            <table key={i} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, margin: '4px 0' }}>
              <thead><tr>{row.table.head.map((h) => <th key={h} style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid var(--rule)', color: 'var(--ink-mute)', fontWeight: 600, fontSize: 10 }}>{h}</th>)}</tr></thead>
              <tbody>{row.table.rows.map((cells, ri) => <tr key={ri}>{cells.map((c, ci) => <td key={ci} style={{ padding: '3px 6px', borderBottom: '1px solid var(--rule)' }}>{c}</td>)}</tr>)}</tbody>
            </table>
          );
        }
        if (row.list) {
          return <div key={i} style={{ fontSize: 12, margin: '3px 0' }}>{row.label && <b>{row.label}: </b>}{row.list.join(', ')}</div>;
        }
        return <div key={i} style={{ fontSize: 12.5, margin: '3px 0' }}>{row.label && <b>{row.label}: </b>}{row.value}</div>;
      })}
    </div>
  );
}

export default function ReportComposerCanvas({ tierCode, answers, findings, onMsg }: ReportComposerCanvasProps) {
  const model = useMemo(() => phaseModelForTier(tierCode), [tierCode]);
  const level = tierLevel(tierCode);
  const sections = useMemo(() => buildReportSections(tierCode, answers, findings), [tierCode, answers, findings]);

  // Composer pick state. L1 = locked (always the default set, not toggleable).
  // L2/L3 = the staff toggles; start from defaultSelected.
  const locked = model.kind === 'l1-locked';
  const [selected, setSelected] = useState<Set<string>>(() => new Set(sections.filter((s) => s.defaultSelected).map((s) => s.key)));
  const effectiveSelected = useMemo(
    () => (locked ? new Set(sections.filter((s) => s.defaultSelected).map((s) => s.key)) : selected),
    [locked, sections, selected],
  );

  function toggle(key: string) {
    if (locked) return; // frozen
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    onMsg?.(`Section ${effectiveSelected.has(key) ? 'removed from' : 'added to'} the report.`);
  }

  const placed = sections.filter((s) => effectiveSelected.has(s.key));
  const available = sections.filter((s) => !effectiveSelected.has(s.key));

  return (
    <Box title="Report Composer — assemble from the pool" note={`${tierCode} · ${model.label} · ${placed.length} of ${sections.length} sections`} allowOverflow>
      {/* tier behaviour */}
      <div style={{ fontSize: 12, color: 'var(--accent-deep, #274b63)', background: 'var(--accent-wash, #eef3f8)', border: '1px solid var(--accent-line, #d3e0ec)', borderRadius: 'var(--r-sm)', padding: '9px 12px', marginBottom: 12 }}>
        <b>L{level} · {model.label}</b> — {locked ? 'auto-filled & locked (frozen template). No picking — instant, consistent report.' : level === 2 ? 'pre-filled; you may add/remove a few sections (curated).' : 'open pick-and-choose, built up across the engagement.'}
      </div>

      {/* two-pane pick (L2/L3 only) */}
      {!locked && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 12, marginBottom: 14 }}>
          <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: '#fcfdfe' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '8px 12px', borderBottom: '1px solid var(--rule)', color: 'var(--accent-deep)' }}>Findings Pool — available</div>
            {available.length === 0 ? <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-mute)' }}>All sections placed.</div> :
              available.map((s) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderBottom: '1px solid var(--rule)' }}>
                  <span style={{ flex: 1, fontSize: 12.5 }}>{s.title}</span>
                  <SourceChip source={s.source} />
                  {s.stubDepth && <Tag kind="tag" title="From a confirmed record; deep engine data is Inc-3b.">record</Tag>}
                  <Btn3 intent="ghost" size="sm" onClick={() => toggle(s.key)}>＋ Add</Btn3>
                </div>
              ))}
          </div>
          <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: '#fcfdfe' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '8px 12px', borderBottom: '1px solid var(--rule)', color: 'var(--accent-deep)', display: 'flex', justifyContent: 'space-between' }}>
              <span>Report — selected</span><Tag kind="val">{placed.length} of {sections.length}</Tag>
            </div>
            {placed.map((s, i) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderBottom: '1px solid var(--rule)', background: 'var(--ok-wash, #eaf5ef)' }}>
                <span style={{ fontSize: 11, color: 'var(--ink-mute)', fontFamily: 'monospace' }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 12.5 }}>{s.title}</span>
                <SourceChip source={s.source} />
                <button type="button" onClick={() => toggle(s.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)' }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THE READABLE REPORT */}
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--accent-deep)', margin: '4px 0 8px' }}>Report preview</div>
      <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: '#fff', padding: 0, overflow: 'hidden' }}>
        {/* cover band */}
        <div style={{ background: 'linear-gradient(135deg, #4a6741 0%, #6b8a5f 100%)', color: '#fff', padding: '16px 18px' }}>
          <div style={{ fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', color: '#e6c97a', fontWeight: 600 }}>Aayatan Veda · {tierCode}</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{model.kind === 'l3-open' ? 'The Design Path · Sthapana' : model.kind === 'l2-curated' ? 'The Alignment Path' : 'Assessment report'}</div>
          <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 2 }}>{String(answers.client_name || answers.property_address || 'Sample project')}</div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          {placed.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No sections selected. {model.kind === 'l1-locked' ? 'L1 auto-fills its frozen set — add intake data / confirm findings to populate it.' : 'Add sections from the pool above.'}</div>
          ) : placed.map((s, i) => (
            <div key={s.key} style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, borderBottom: '1px solid var(--rule)', paddingBottom: 4, marginBottom: 6 }}>
                <span style={{ background: '#4a6741', color: '#fff', fontWeight: 600, fontSize: 11, padding: '1px 7px', borderRadius: 3 }}>{i + 1}</span>
                <span style={{ fontSize: 15, fontWeight: 700, flex: 1, fontFamily: 'Georgia, serif' }}>{s.title}</span>
                <SourceChip source={s.source} />
                {s.stubDepth && <span style={{ fontSize: 9.5, color: 'var(--warn, #9a6a12)' }}>record-depth (Inc-3b)</span>}
              </div>
              <SectionBody section={s} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-mute)' }}>
        Production generation + delivery go through the existing report engine (now phase-aware). This canvas is the assembly + the readable preview.
        Astro-Vastu / Health sections render the CONFIRMED finding content; deep per-occupant astro/health engine data is Inc-3b.
      </div>
    </Box>
  );
}
